import { SEODataRetriever } from '@care/mfe-data-transfer-seo';
import { CARE_DATES, VerticalsAbbreviation, SERVICE_TYPE_BY_VERTICAL } from '@/constants';
import { careDateToGQLFormat } from '@/utilities/account-creation-utils';
import { CareKind, CareKindToSubServiceType, Child, DefaultCareKind } from '@/types/seekerCC';
import { ServiceTypes } from '@/types/seekerPC';
import {
  EnrollmentSource,
  HOW_DID_YOU_HEAR_ABOUT_US,
  PetCareSubtype,
  ServiceType,
} from '@/__generated__/globalTypes';
import userEmailStore from '@/lib/UserEmailStore';
import { seekerCreateVariables } from '@/__generated__/seekerCreate';
import socialProviderStore from '@/lib/SocialProviderStore';

export interface SeekerCreatePayload {
  vertical: VerticalsAbbreviation;
  zipcode: string;
  firstName: string;
  lastName: string;
  email: string;
  howDidYouHearAboutUs?: string;
  careKind?: CareKind | PetCareSubtype;
  careDate?: CARE_DATES;
  referrerCookie?: string | null;
  addressLine1?: string | null;
  password?: string | null;
  enrollmentSource?: EnrollmentSource | null;
  careRecipients?: Child[] | null;
  googleId?: string;
  appleId?: string;
  isExpecting?: boolean | null;
  additionalVerticals?: Array<ServiceType>;
  gatedByPaywall?: boolean;
}

const getSubServiceTypeAndEnrollmentSource = (
  vertical: VerticalsAbbreviation,
  careKind: CareKind | PetCareSubtype
) => {
  const seoRetrieverRef = new SEODataRetriever();
  const providerId = seoRetrieverRef.retrieveData()?.providerId;

  switch (vertical) {
    case 'CC':
      return {
        subServiceType: CareKindToSubServiceType[careKind as CareKind],
        ...(careKind === DefaultCareKind.ONE_TIME_BABYSITTERS && providerId
          ? {
              enrollmentSource: EnrollmentSource.INSTANT_BOOK,
            }
          : {}),
      };
    case 'DC':
      return {
        subServiceType: CareKindToSubServiceType[careKind as CareKind],
      };
    case 'IB':
      return {
        subServiceType: CareKindToSubServiceType[careKind as CareKind],
        enrollmentSource: EnrollmentSource.INSTANT_BOOK,
      };
    case 'PC': {
      let careSubType = careKind as PetCareSubtype;
      if ((careKind as string) === ServiceTypes.TRAINING) {
        // This is a quick fix to the issue reported on https://carecom.atlassian.net/browse/GROW-6164
        // TODO: improve this code, maybe we could get rid of ServiceTypes and only use PetCareSubtype
        careSubType = 'DOG_TRAINING' as PetCareSubtype;
      }
      return {
        petCareSubtype: careSubType,
      };
    }
    default:
      return {};
  }
};

export const getSeekerAccountCreatePayload = ({
  vertical,
  zipcode,
  firstName,
  lastName,
  email,
  howDidYouHearAboutUs,
  careKind,
  careDate,
  referrerCookie,
  addressLine1,
  password,
  enrollmentSource,
  careRecipients,
  isExpecting,
  additionalVerticals,
  gatedByPaywall,
}: SeekerCreatePayload): { variables: seekerCreateVariables } => {
  const socialProviderData = socialProviderStore.getSocialProviderData();
  return {
    variables: {
      input: {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: email || userEmailStore.getEmail(),
        zipcode,
        howDidYouHearAboutUs: (howDidYouHearAboutUs as HOW_DID_YOU_HEAR_ABOUT_US) || undefined,
        serviceType: SERVICE_TYPE_BY_VERTICAL[vertical],
        careDate: careDate ? careDateToGQLFormat(careDate) : null,
        referrerCookie,
        ...(careKind ? getSubServiceTypeAndEnrollmentSource(vertical, careKind) : {}),
        ...(enrollmentSource ? { enrollmentSource } : {}),
        ...(socialProviderData?.googleId ? { googleId: socialProviderData.googleId } : {}),
        ...(socialProviderData?.appleId ? { appleId: socialProviderData.appleId } : {}),
        addressLine1,
        password,
        careRecipients,
        isExpecting:
          vertical === 'CC' && Array.isArray(careRecipients) && careRecipients.length > 0
            ? isExpecting
            : undefined,
        additionalVerticals: additionalVerticals || undefined,
        gatedByPaywall: gatedByPaywall || undefined,
      },
    },
  };
};
